/**
 * Authentication context provider
 */

import { createContext, useState, useEffect, ReactNode } from 'react';
import * as authApi from '../services/authApi';
import type { User, AuthSession, LoginRequest, RegisterRequest } from '../types/auth';
import { getJWTExpiration } from '../utils/jwtHelpers';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const SESSION_KEY = 'auth_session';

/**
 * Save session to localStorage
 * Extracts expiration from JWT token payload
 */
function saveSession(token: string, refreshToken: string): void {
  // Get expiration from JWT token
  const expiresAt = getJWTExpiration(token);

  // If we can't decode expiration, fall back to 30 days
  const fallbackExpiration = Date.now() + (720 * 60 * 60 * 1000);

  const session: AuthSession = {
    token,
    refreshToken,
    expiresAt: expiresAt || fallbackExpiration,
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * Clear session from localStorage
 */
function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Load session from localStorage
 */
function loadSession(): AuthSession | null {
  const sessionStr = localStorage.getItem(SESSION_KEY);
  if (!sessionStr) return null;

  try {
    const session: AuthSession = JSON.parse(sessionStr);
    // Check if expired
    if (Date.now() >= session.expiresAt) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate session on mount
  useEffect(() => {
    const session = loadSession();
    if (!session) {
      setIsLoading(false);
      return;
    }

    // Validate token with backend
    authApi
      .validateSession()
      .then((userData) => {
        setUser(userData);
        setError(null);
      })
      .catch((err) => {
        console.error('Session validation failed:', err);
        clearSession();
        setError('Session expired. Please login again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login(credentials);
      saveSession(response.token, response.refresh_token);
      setUser(response.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.register(data);
      saveSession(response.token, response.refresh_token);
      setUser(response.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const session = loadSession();

    try {
      // Call logout API to revoke tokens
      if (session) {
        await authApi.logout({ refresh_token: session.refreshToken });
      }
    } catch (err) {
      console.error('Logout API call failed:', err);
      // Continue with local logout even if API fails
    } finally {
      clearSession();
      setUser(null);
      setError(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await authApi.getUserProfile();
      setUser(userData);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  };

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
